import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {forkJoin} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    TradingService,
    PermissionModel,
    TradingAccountAccessModel
} from '@spa/api';

import {InArrayValidatorKey} from '@spa/validators';

import {Helper} from '../../../shared/classes';
import {CheckboxControl} from '../../../shared/classes/controls';
import {ColorCodesEnum, ComponentTypesEnum} from '../../../shared/enums/common';

import {AccountsAccessRightsControlsCollectionItemInterface} from '../interfaces';
import {AccountAccessRightControl, AccountsAccessRightsControlsCollection} from '../classes';
import {TradingAccountTypesEnum, UpdateClientAccountRightsFormErrorStatesEnum} from '../enums';

import {
    UpdateAccountAccessRightIdSelector,
    UpdateAccountAccessRightsArraySelector,
    UpdateAccountInArrayErrorPlaceholder,
    UpdateAccountsAccessRightsArraySelector
} from '../../../../assets/descriptors';
import { PortalPermissionsEnum } from 'appShared/enums';

export class UpdateClientAccountsRightsForm {
    private readonly tradingService: TradingService;

    private hasUnAssignRequest: boolean;

    public form: FormGroup;
    public controlsCollection: AccountsAccessRightsControlsCollection;

    public accountsRightsArraySelector: string;
    public accountRightsArraySelector: string;

    public errors: { [accessRightIndex: string]: string[] };

    constructor(tradingService: TradingService) {
        this.tradingService = tradingService;

        this.errors = {};
        this.hasUnAssignRequest = false;
        this.accountRightsArraySelector = UpdateAccountAccessRightsArraySelector;
        this.accountsRightsArraySelector = UpdateAccountsAccessRightsArraySelector;
    }

    private createControlCollectionItems(
        accountsAccessRights: TradingAccountAccessModel[],
        permissions: PermissionModel[],
        accountPermissionStrings: string[]
    ): AccountsAccessRightsControlsCollectionItemInterface[] {
        return accountsAccessRights.map((accessRight: TradingAccountAccessModel, accessRightIndex: number) => {
            const controls = permissions.filter((permission: PermissionModel) => {
                return (accountPermissionStrings.indexOf(permission.permissionString) >= 0);
            }).map((permission: PermissionModel, index: number) => {
                const controlPosition = Helper.enumsHelper().permissionsEnumHelper().getPortalAccountPermissionPosition(
                    !accessRight.isCashAccount ? TradingAccountTypesEnum.Trading : TradingAccountTypesEnum.Cash,
                    permission.permissionString
                );

                let permissionName = '';

                if (!accessRight.isCashAccount) {
                    permissionName = Helper.textsHelper()
                        .getPortalTradingAccountPermissionName(permission.permissionString);
                } else {
                    permissionName = Helper.textsHelper()
                        .getPortalCashAccountPermissionName(permission.permissionString);
                }

                const control = new CheckboxControl({
                    name: index + '',
                    value: permission.id,
                    placeholder: permissionName,
                    validators: [],
                    componentType: ComponentTypesEnum.Checkbox,
                    checked: (accessRight.permissionIds.indexOf(permission.id) >= 0),
                    disabled: true,
                    color: ColorCodesEnum.Primary,
                    expectBooleanValue: false
                });

                return new AccountAccessRightControl(controlPosition, control);
            });

            return {
                index: accessRightIndex,
                accountId: accessRight.accountId,
                accountReference: accessRight.referenceId,
                controls
            };
        });
    }

    private createUpdateAccessRightRequest(
        accessRight: TradingAccountAccessModel,
        customerId: number,
        userRelationType: number,
        permissionIds: number[]
    ): ServerResponse<boolean> {
        const currentPermissionIds = accessRight.permissionIds;

        accessRight.customerId = customerId;
        accessRight.userRelation = userRelationType;
        accessRight.permissionIds = permissionIds;

        return this.tradingService.updateAccountAccessRight(accessRight).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    accessRight.permissionIds = currentPermissionIds;

                    return Helper.errorsHelper().createErrorResponse<null>(
                        response,
                        UpdateClientAccountRightsFormErrorStatesEnum.UpdateRightsFailed,
                        accessRight.referenceId
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private createUnAssignAccessRightRequest(
        id: number,
        reference: string,
        accountsAccessRights: TradingAccountAccessModel[]
    ): ServerResponse<boolean> {
        return this.tradingService.unAssignAccountAccessRight(id).pipe(
            map((response: ServerResponseData<null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<null>(
                        response,
                        UpdateClientAccountRightsFormErrorStatesEnum.UpdateRightsFailed,
                        reference
                    );
                }

                this.hasUnAssignRequest = true;

                const filteredAccountsRights = accountsAccessRights.filter((accessRight: TradingAccountAccessModel) => {
                    return accessRight.accountAccessId !== id;
                });

                accountsAccessRights.splice(0, accountsAccessRights.length);
                accountsAccessRights.push(...filteredAccountsRights);

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private updateAllCashAccountsRights(formAccessRights: any, cashAccountCreatePermission: boolean, portalPermissions: PermissionModel[]) {
        if(!cashAccountCreatePermission){
            return;
        }
        var permissions = portalPermissions.filter((permission: PermissionModel) => {
            return (Helper.enumsHelper().permissionsEnumHelper().getPortalCashAccountPermissions().indexOf(permission.permissionString) >= 0);
        })

        var fillPermissions = [
             portalPermissions.find(f => f.permissionString === PortalPermissionsEnum.CashAccountsView),
             portalPermissions.find(f => f.permissionString === PortalPermissionsEnum.CashAccountsEdit)
        ]; 
        formAccessRights.forEach((account) => { 
            fillPermissions.forEach((permission) => {  
                var index = permissions.indexOf(permission); 
                account.accountAccessRights[index] =  permission.id; 
            });
        });
    }

    toggleCollectionControls(disabled: boolean) {
        this.controlsCollection.getItems().forEach((item: AccountsAccessRightsControlsCollectionItemInterface) => {
            item.controls.forEach((control: AccountAccessRightControl) => {
                control.control.disabled = disabled;
            });
        });
    }

    getControlCollectionItemErrors(accountId): string[] {
        return this.errors[accountId];
    }

    initForm(
        accountsType: number,
        accountsAccessRights: TradingAccountAccessModel[],
        permissions: PermissionModel[]
    ): void {
        this.form = new FormGroup({
            [UpdateAccountsAccessRightsArraySelector]: new FormArray([])
        });

        this.controlsCollection = new AccountsAccessRightsControlsCollection();

        let collectionItems = [];

        if (accountsType !== TradingAccountTypesEnum.Cash) {
            collectionItems = this.createControlCollectionItems(
                accountsAccessRights,
                permissions,
                Helper.enumsHelper().permissionsEnumHelper().getPortalTradingAccountPermissions()
            );
        } else {
            collectionItems = this.createControlCollectionItems(
                accountsAccessRights,
                permissions,
                Helper.enumsHelper().permissionsEnumHelper().getPortalCashAccountPermissions()
            );
        }

        this.controlsCollection.setItems(collectionItems);

        this.controlsCollection.getItems().forEach((item: AccountsAccessRightsControlsCollectionItemInterface) => {
            this.errors[item.accountId] = [];

            const formGroup = new FormGroup({
                [UpdateAccountAccessRightIdSelector]: new FormControl(item.accountId)
            });

            const formArray = new FormArray([]);

            item.controls.forEach((control: AccountAccessRightControl) => {
                formArray.insert(parseInt(control.control.name, 10), new FormControl(control.control.value));
            });

            formGroup.addControl(UpdateAccountAccessRightsArraySelector, formArray);

            (this.form.get(UpdateAccountsAccessRightsArraySelector) as FormArray).insert(item.index, formGroup);
        });
    }

    validateForm(
        accountsType: number,
        permissions: PermissionModel[],
        messageFunction: (controlLabel?: string) => { [key: string]: string; }
    ): boolean {
        const accountTypePermissionsIds = permissions.filter((permission: PermissionModel) => {
            if (accountsType !== TradingAccountTypesEnum.Cash) {
                return (Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalTradingAccountPermissions().indexOf(permission.permissionString) >= 0);
            } else {
                return (Helper.enumsHelper().permissionsEnumHelper()
                    .getPortalCashAccountPermissions().indexOf(permission.permissionString) >= 0);
            }
        }).map((permission: PermissionModel) => permission.id);

        const errorChecks = (this.form.get(UpdateAccountsAccessRightsArraySelector) as FormArray).controls
            .map((formGroup: FormGroup) => {
                const accessRightId = formGroup.get(UpdateAccountAccessRightIdSelector).value;
                const accessRightPermissionsArray = (formGroup.get(UpdateAccountAccessRightsArraySelector) as FormArray);

                const permissionIds = accessRightPermissionsArray.value.filter((id: number) => (id !== null));

                this.errors[accessRightId].splice(0, this.errors[accessRightId].length);

                if (permissionIds.length !== 0) {
                    const accessRightPermissions = permissionIds.every((permissionId: number) => {
                        return accountTypePermissionsIds.indexOf(permissionId) >= 0;
                    });

                    if (!accessRightPermissions) {
                        const errorMessage = messageFunction(UpdateAccountInArrayErrorPlaceholder)[InArrayValidatorKey];

                        this.errors[accessRightId].push(errorMessage);

                        return false;
                    }
                }

                return true;
            });

        return errorChecks.indexOf(false) < 0;
    }

    createRequest(
        customerId: number,
        userRelationType: number,
        accountsAccessRights: TradingAccountAccessModel[],
        portalPermissions: PermissionModel[],
        cashAccountPermissionChanged = false,
        cashAccountCreatePermission  = false
    ): ServerResponse<boolean> {
        const rightIdKey = UpdateAccountAccessRightIdSelector;
        const rightPermissionIdsKey = UpdateAccountAccessRightsArraySelector;
        const formAccessRights = this.form.value[UpdateAccountsAccessRightsArraySelector];

        if (cashAccountPermissionChanged) {
            this.updateAllCashAccountsRights(formAccessRights, cashAccountCreatePermission, portalPermissions);
        }

        const requests = formAccessRights.map((right: { [rightIdKey]: number, [rightPermissionIdsKey]: number[] }) => {
            const filteredRights = accountsAccessRights.filter((accessRight: TradingAccountAccessModel) => {
                return accessRight.accountId === right[rightIdKey];
            });

            if (filteredRights.length > 0) {
                const filteredPermissionIds = right[rightPermissionIdsKey].filter((id: number) => (id !== null));

                if (filteredPermissionIds.length > 0) {

                    const isPermissionChanged = !Helper.compareSimpleArrays<number>(
                        filteredPermissionIds,
                        filteredRights[0].permissionIds
                    );

                    if (isPermissionChanged) {
                        return this.createUpdateAccessRightRequest(
                            filteredRights[0],
                            customerId,
                            userRelationType,
                            filteredPermissionIds
                        );
                    }
                } else {
                    return this.createUnAssignAccessRightRequest(
                        filteredRights[0].accountAccessId,
                        filteredRights[0].referenceId,
                        accountsAccessRights
                    );
                }
            }

            return null;
        }).filter((request: ServerResponse<boolean>) => (request !== null));

        if (requests.length > 0) {
            return forkJoin(requests as ServerResponse<boolean>[]).pipe(
                map((responses: ServerResponseData<boolean>[]) => {
                    return !Helper.errorsHelper().checkForErrorResponses<boolean>(responses) ?
                        Helper.createSuccessResponse<boolean>(this.hasUnAssignRequest) :
                        Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                })
            );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }

}
